'use client';

import Link from 'next/link';
import { useEffect, useState, useCallback } from 'react';
import { usePathname } from 'next/navigation';
import { AnimatePresence, motion, Transition } from 'framer-motion';
import { useScramble } from 'use-scramble';

import { Logo } from '@/components/global/logo';
import { ThemeToggle } from '../../components/global/theme-toggle';
import { ROUTES } from '@/routes';
import { GLOBAL_NAV, SOCIAL_LINKS } from '@/utils/constants';
import { NavItemsProps, GlobalNavItem } from '@/utils/types';
import { IconClose } from '../../components/icons';
import { Button } from '../../components/button';
import AnimatedLine from '../../components/animations/animated-line';

const NavItem = ({
  item,
  pathname,
  onClick,
  className,
}: {
  item: GlobalNavItem;
  pathname: string;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
  className: string;
}) => {
  const isActive = pathname === item.href;

  const { ref, replay } = useScramble({
    text: item.label,
    playOnMount: false,
    speed: 0.6,
    scramble: 3,
    tick: 1,
    range: [33, 64],
  });

  return (
    <Link
      href={item.href ?? '#'}
      target={item.target ?? '_self'}
      onClick={onClick}
      className={`${className} ${item.className ?? ''}`}>
      <span className="text-transparent">{item.label}</span>
      <span
        ref={ref}
        onMouseLeave={replay}
        aria-hidden
        className="absolute left-1/2 -translate-x-1/2 lg:bottom-1.5 lg:left-0 lg:translate-x-0">
        {item.label}
      </span>
      <div
        className={`absolute right-0 -bottom-0.5 left-0 mx-auto hidden size-1.5 origin-center rounded-full bg-red-500 transition-transform lg:block ${
          isActive ? 'scale-100' : 'scale-0 group-hover:scale-100'
        }`}
      />
    </Link>
  );
};

export const NavItems = ({ className = '', onClick }: NavItemsProps) => {
  const pathname = usePathname();

  return GLOBAL_NAV.map(item => (
    <NavItem
      key={item.href}
      item={item}
      pathname={pathname}
      onClick={onClick}
      className={className}
    />
  ));
};

export const Header: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const pathname = usePathname();

  const PANEL_TRANSITION: Transition = {
    duration: 0.45,
    ease: [0.22, 1, 0.36, 1],
  };

  const toggleMenu = () => {
    setIsOpen(prev => {
      const nextState = !prev;
      document.body.style.overflow = nextState ? 'hidden' : 'unset';
      return nextState;
    });
  };

  // Ensure scroll lock is released on page changes or unmount
  useEffect(() => {
    setIsOpen(false);
    document.body.style.overflow = 'unset';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [pathname]);

  const handleScroll = useCallback(() => {
    setIsScrolled(window.scrollY > 100);
  }, []);

  useEffect(() => {
    // Sync initial state on load/refresh
    handleScroll();

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const isCompact = isScrolled && !isOpen;

  return (
    <>
      <header className="sticky top-0 z-20 w-full transform overflow-y-visible transition-all duration-300 ease-out">
        <div
          className={`container transition-all ${isOpen ? 'bg-cream dark:bg-dark' : ''}`}>
          <motion.div
            initial={false}
            animate={{
              maxWidth: isCompact ? '42rem' : '76rem',
              padding: isCompact ? '8px 24px' : '8px 0px',
              top: isCompact ? '8px' : '0px',
            }}
            transition={{ duration: 0.4, ease: 'easeInOut' }}
            className="relative mx-auto"
            aria-label="Global">
            <motion.div
              aria-hidden
              animate={{ opacity: isCompact ? 1 : 0 }}
              className="bg-cream/60 dark:bg-dark/20 pointer-events-none absolute inset-0 z-0 rounded-full backdrop-blur-[50px]"
            />

            <div className="relative z-10 flex items-center justify-between gap-x-4 lg:gap-x-6">
              <div className="flex-none">
                <Link
                  href={ROUTES.ROOT}
                  className="block"
                  onClick={() => isOpen && toggleMenu()}>
                  <Logo className="h-10" mode="logo" />
                </Link>
              </div>

              <div className="hidden w-full justify-end sm:flex" aria-hidden>
                <div className="bg-copper/30 relative h-0.5 w-full max-w-48 origin-right transform animate-[expand-width_300ms_ease-in-out_500ms_both] overflow-hidden rounded-full dark:bg-white/20">
                  <AnimatedLine
                    delay={1.5}
                    positionClass="top-0 left-0"
                    widthClass="w-48"
                    heightClass="h-0.5"
                    fromClass="from-primary-500 dark:from-white via-primary-500 dark:via-white"
                    toClass="to-primary-500/0 dark:to-white/0"
                    direction="left-to-right"
                    distance={280}
                  />
                </div>
              </div>

              <nav className="hidden items-center justify-center sm:flex sm:gap-x-8 sm:px-4">
                <h2 className="sr-only">Site navigation</h2>
                <NavItems className="group text-dark/65 hover:text-dark relative rounded-md py-1.5 text-sm font-bold tracking-wider uppercase transition duration-300 ease-in-out dark:text-white/80 dark:hover:text-white" />
              </nav>

              <div className="hidden w-full justify-start sm:flex" aria-hidden>
                <div className="bg-copper/30 relative h-0.5 w-full max-w-48 origin-left transform animate-[expand-width_300ms_ease-in-out_500ms_both] overflow-hidden rounded-full dark:bg-white/20">
                  <AnimatedLine
                    delay={0.5}
                    positionClass="top-0 right-0"
                    widthClass="w-48"
                    heightClass="h-0.5"
                    fromClass="from-primary-500/0 dark:from-white/0"
                    toClass="to-primary-500 dark:to-white"
                    direction="right-to-left"
                    distance={180}
                  />
                </div>
              </div>

              <div className="dark:text-cream ml-auto flex items-center hover:dark:text-white">
                <ThemeToggle />
              </div>

              <Button
                onClick={toggleMenu}
                size="sm"
                className="flex-none sm:hidden">
                <span className="sr-only">Navigation</span>
                <AnimatePresence mode="wait" initial={false}>
                  {isOpen ? (
                    <motion.span
                      key="close"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="flex items-center justify-center gap-x-1">
                      Close
                      <IconClose
                        className="text-dark size-5"
                        strokeWidth={1.5}
                      />
                    </motion.span>
                  ) : (
                    <motion.span
                      key="menu"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.2 }}>
                      Menu
                    </motion.span>
                  )}
                </AnimatePresence>
              </Button>
            </div>
          </motion.div>
        </div>
      </header>

      <AnimatePresence>
        {isOpen && (
          <div className="fixed inset-0 top-14 z-50 sm:hidden">
            <motion.div
              initial="initial"
              animate="visible"
              exit="hidden"
              variants={{
                initial: { opacity: 0 },
                visible: {
                  opacity: 1,
                  transition: { duration: 0.45, ease: 'easeInOut' },
                },
                hidden: {
                  opacity: 0,
                  transition: { duration: 0.25, ease: 'easeInOut' },
                },
              }}
              className="absolute top-1/2 left-1/2 z-50 w-full -translate-x-1/2 -translate-y-1/2 px-6">
              <nav className="flex flex-col gap-y-6">
                <NavItems
                  onClick={toggleMenu}
                  className="text-cream dark:text-dark hover:text-primary-500 relative flex w-full justify-center font-serif text-4xl"
                />
              </nav>

              <ul className="dark:border-copper/30 mt-24 flex w-full flex-1 border-y border-white/10">
                {SOCIAL_LINKS.map(item => (
                  <Link
                    key={item.label}
                    className="dark:border-copper/30 dark:text-dark/50 dark:hover:text-dark hover:bg-dark-lighten dark:hover:bg-cream-dark flex flex-1 justify-center border-r border-white/10 px-3 py-3 text-white/50 transition-all last-of-type:border-none hover:text-white"
                    href={item.href}
                    target="_blank"
                    rel="noreferrer noopener"
                    aria-label={`${item.label} (opens in a new tab)`}>
                    {item.label}
                  </Link>
                ))}
              </ul>
            </motion.div>

            {/* Transitional Panels */}
            <motion.div
              initial={{ y: '-100%' }}
              animate={{ y: 0 }}
              exit={{ y: '-100%' }}
              transition={PANEL_TRANSITION}
              className="bg-dark dark:bg-cream absolute top-0 right-0 left-0 h-[50svh]"
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={PANEL_TRANSITION}
              className="bg-dark dark:bg-cream absolute top-0 right-0 bottom-0 w-[50svw]"
            />
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={PANEL_TRANSITION}
              className="bg-dark dark:bg-cream absolute right-0 bottom-0 left-0 h-[50svh]"
            />
            <motion.div
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={PANEL_TRANSITION}
              className="bg-dark dark:bg-cream absolute top-0 bottom-0 left-0 w-[50svw]"
            />
          </div>
        )}
      </AnimatePresence>
    </>
  );
};
